import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  Plus,
  MapPin,
  CalendarDays,
  Pencil,
  Trash2,
  X,
  Mic,
  Users,
  BookOpen,
  Wrench,
  Coffee,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import type { CreateSessionInput, EventSessionDto, SessionType } from "@mvk/shared";
import { tryAdminEventById } from "../../services/adminEventsService";
import { adminSessionsApi } from "../../api/adminSessions";
import { isApiEnabled } from "../../api/config";

const sessionTypeConfig: Record<
  SessionType,
  { label: string; color: string; icon: React.ReactNode }
> = {
  presentation: { label: "Доклад", color: "bg-indigo-100 text-indigo-700", icon: <Mic size={12} /> },
  panel: { label: "Панельная", color: "bg-violet-100 text-violet-700", icon: <Users size={12} /> },
  workshop: { label: "Воркшоп", color: "bg-emerald-100 text-emerald-700", icon: <Wrench size={12} /> },
  masterclass: { label: "Мастер-класс", color: "bg-amber-100 text-amber-700", icon: <BookOpen size={12} /> },
  networking: { label: "Нетворкинг", color: "bg-pink-100 text-pink-700", icon: <Coffee size={12} /> },
  keynote: { label: "Пленарная", color: "bg-blue-100 text-blue-800", icon: <Mic size={12} /> },
};

type SessionFormState = {
  title: string;
  type: SessionType;
  startTime: string;
  endTime: string;
  location: string;
  description: string;
  speakerName: string;
  speakerPosition: string;
};

function emptyForm(): SessionFormState {
  return {
    title: "",
    type: "presentation",
    startTime: "10:00",
    endTime: "11:00",
    location: "",
    description: "",
    speakerName: "",
    speakerPosition: "",
  };
}

function formFromSession(session: EventSessionDto): SessionFormState {
  const speaker = session.speakers[0];
  return {
    title: session.title,
    type: session.type,
    startTime: new Date(session.startTime).toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    endTime: new Date(session.endTime).toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    location: session.location,
    description: session.description,
    speakerName: speaker?.fullName ?? "",
    speakerPosition: speaker?.position ?? "",
  };
}

function combineDayAndTime(day: Date, time: string): string {
  const [h, m] = time.split(":").map(Number);
  const d = new Date(day);
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d.toISOString();
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

interface SessionModalProps {
  day: Date;
  session?: EventSessionDto;
  onClose: () => void;
  onSave: (payload: CreateSessionInput) => Promise<void>;
}

function SessionModal({ day, session, onClose, onSave }: SessionModalProps) {
  const isEdit = Boolean(session);
  const [form, setForm] = useState<SessionFormState>(
    session ? formFromSession(session) : emptyForm(),
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Введите название сессии");
      return;
    }
    if (!form.startTime || !form.endTime) {
      toast.error("Укажите время начала и окончания");
      return;
    }

    setSaving(true);
    try {
      const speakers =
        form.speakerName.trim().length > 0
          ? [
              {
                id: session?.speakers?.[0]?.id ?? crypto.randomUUID(),
                fullName: form.speakerName.trim(),
                position: form.speakerPosition.trim() || "Спикер",
              },
            ]
          : [];

      await onSave({
        title: form.title.trim(),
        type: form.type,
        startTime: combineDayAndTime(day, form.startTime),
        endTime: combineDayAndTime(day, form.endTime),
        location: form.location.trim() || undefined,
        description: form.description.trim() || undefined,
        speakers,
      });
      toast.success(isEdit ? "Сессия обновлена" : "Сессия добавлена");
      onClose();
    } catch {
      toast.error("Не удалось сохранить сессию");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-[15px] text-gray-900">
            {isEdit ? "Редактировать сессию" : "Добавить сессию"}
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] text-gray-500">
            День: {day.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })}
          </p>
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Название*</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Тип</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as SessionType })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 bg-white"
              >
                {Object.entries(sessionTypeConfig).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div />
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Начало</label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Конец</label>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Место</label>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Конференц-зал A"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Описание</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Спикер</label>
              <input
                value={form.speakerName}
                onChange={(e) => setForm({ ...form, speakerName: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Должность</label>
              <input
                value={form.speakerPosition}
                onChange={(e) => setForm({ ...form, speakerPosition: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100">
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[13px]"
          >
            {saving ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminSchedulePage() {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [eventTitle, setEventTitle] = useState("");
  const [eventStart, setEventStart] = useState<Date | null>(null);
  const [eventEnd, setEventEnd] = useState<Date | null>(null);
  const [sessions, setSessions] = useState<EventSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const [modalSession, setModalSession] = useState<EventSessionDto | undefined>();
  const [showModal, setShowModal] = useState(false);

  const loadData = useCallback(async () => {
    if (!eventId || !isApiEnabled()) return;
    setLoading(true);
    try {
      const [event, list] = await Promise.all([
        tryAdminEventById(eventId),
        adminSessionsApi.list(eventId),
      ]);
      if (event) {
        setEventTitle(event.title);
        setEventStart(new Date(event.startDate));
        setEventEnd(new Date(event.endDate));
      }
      setSessions(list.sessions);
    } catch {
      toast.error("Не удалось загрузить программу");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const days = useMemo(() => {
    if (!eventStart || !eventEnd) return [] as Date[];
    const start = new Date(eventStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(eventEnd);
    end.setHours(0, 0, 0, 0);
    const result: Date[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      result.push(new Date(d));
    }
    return result.length > 0 ? result : [start];
  }, [eventStart, eventEnd]);

  const activeDay = days[activeDayIndex] ?? days[0] ?? new Date();

  const daySessions = useMemo(() => {
    return sessions
      .filter((s) => isSameCalendarDay(new Date(s.startTime), activeDay))
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [sessions, activeDay]);

  const openCreate = () => {
    setModalSession(undefined);
    setShowModal(true);
  };

  const openEdit = (session: EventSessionDto) => {
    setModalSession(session);
    setShowModal(true);
  };

  const handleSave = async (payload: Parameters<SessionModalProps["onSave"]>[0]) => {
    if (!eventId) return;
    if (modalSession) {
      const { session } = await adminSessionsApi.update(eventId, modalSession.id, payload);
      setSessions((prev) => prev.map((s) => (s.id === session.id ? session : s)));
    } else {
      const { session } = await adminSessionsApi.create(eventId, payload);
      setSessions((prev) => [...prev, session].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      ));
    }
  };

  const handleDelete = async (session: EventSessionDto) => {
    if (!eventId) return;
    if (!window.confirm(`Удалить «${session.title}»?`)) return;
    try {
      await adminSessionsApi.remove(eventId, session.id);
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
      toast.success("Сессия удалена");
    } catch {
      toast.error("Не удалось удалить сессию");
    }
  };

  if (!eventId) {
    return <div className="p-6 text-gray-500">Мероприятие не указано</div>;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24 text-gray-400">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5">
      {showModal && (
        <SessionModal
          day={activeDay}
          session={modalSession}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/events/${eventId}`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-[19px] text-gray-900">Программа</h1>
          <p className="text-[13px] text-gray-400">
            {eventTitle || eventId} · {sessions.length} сессий
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px]"
        >
          <Plus size={14} /> Добавить
        </button>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
        {days.map((d, i) => {
          const count = sessions.filter((s) => isSameCalendarDay(new Date(s.startTime), d)).length;
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => setActiveDayIndex(i)}
              className={`flex-shrink-0 flex flex-col items-center px-4 py-2 rounded-xl transition-colors ${
                activeDayIndex === i
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-200"
              }`}
            >
              <span className="text-[10px] uppercase tracking-wider opacity-70">День {i + 1}</span>
              <span className="text-[14px] mt-0.5">
                {d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}
              </span>
              {count > 0 && (
                <span className={`text-[10px] mt-0.5 ${activeDayIndex === i ? "opacity-70" : "text-gray-400"}`}>
                  {count} сессий
                </span>
              )}
            </button>
          );
        })}
      </div>

      {daySessions.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
          <CalendarDays size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-[14px] text-gray-400">Сессий на этот день нет</p>
          <button
            type="button"
            onClick={openCreate}
            className="mt-3 px-4 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[13px]"
          >
            + Добавить первую сессию
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {daySessions.map((session) => {
            const cfg = sessionTypeConfig[session.type];
            const start = new Date(session.startTime);
            const end = new Date(session.endTime);
            const durationMin = Math.round((end.getTime() - start.getTime()) / 60_000);
            return (
              <div
                key={session.id}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:border-indigo-100 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="text-center flex-shrink-0 w-16">
                    <div className="text-[14px] text-gray-900">
                      {start.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      {end.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="text-[10px] text-gray-300 mt-0.5">{durationMin} мин</div>
                  </div>
                  <div className="w-px self-stretch bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${cfg.color}`}>
                          {cfg.icon} {cfg.label}
                        </span>
                        <h3 className="text-[14px] text-gray-900 mt-1">{session.title}</h3>
                        {session.location && (
                          <div className="flex items-center gap-1 mt-1 text-[12px] text-gray-400">
                            <MapPin size={11} /> {session.location}
                          </div>
                        )}
                        {session.speakers.length > 0 && (
                          <p className="text-[12px] text-gray-500 mt-1">
                            {session.speakers.map((s) => s.fullName).join(", ")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => openEdit(session)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(session)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
